/* oxlint-disable no-promise-executor-return */
import { fetchSupportModules } from '@/entities/support';
import * as pdfjsLib from 'pdfjs-dist';
import { useEffect, useState } from 'react';

/**
 * Drives the support modules tab: loading state, the module list with thumbnails and drill-down selection. On mount it fetches the DIAGNOSTIC_SUPPORT_MODULES list and renders a first-page PDF thumbnail per module, exposes view and back handlers that set or clear the selected module, and derives hasNoData once loading finished on an empty list.
 *
 * @returns Support modules state together with the view and back handlers and the empty-list flag.
 */
export const useSupportModules = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [supportModules, setSupportModules] = useState<any[]>([]);
  const [clickedSupportModule, setClickedSupportModule] = useState<any>(null);

  /**
   * Builds a thumbnail image for a support module by rendering the first page of its PDF. Loads the document with pdf.js, renders page one at scale 2 onto a hidden canvas appended to the support-modules element, converts the canvas to a blob object URL and removes the canvas again, logging and yielding null on failure.
   *
   * @param pdfUrl - Address of the support module PDF to render.
   * @returns Object URL of the rendered first page, or null when rendering failed.
   */
  const handleGetThumbnail = async (pdfUrl: any) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);

      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1); // *: Get first page

      // *: Set the scale for rendering the page (higher value = higher resolution)
      const scale = 2;
      const viewport = page.getViewport({ scale });

      const canvasElement = document.createElement('canvas');
      const supportModulesElement = document.querySelector('.support-modules');
      if (supportModulesElement) {
        canvasElement.style.display = 'none';
        supportModulesElement.appendChild(canvasElement);
      }

      const context = canvasElement.getContext('2d');
      if (!context) return null;
      canvasElement.width = viewport.width;
      canvasElement.height = viewport.height;

      // *: Render the page onto the canvas
      const renderContext = {
        canvasContext: context,
        viewport,
      };

      await page.render(renderContext).promise;

      // *: Convert canvas to image data URL (PNG format)
      /**
       * Derives a browser-local URL for the rendered PDF page image. Awaits the canvas toBlob conversion and wraps the resulting blob in an object URL usable as an image source.
       *
       * @returns Object URL referencing the rendered page blob.
       */
      const blob = await new Promise<Blob | null>((resolve) => canvasElement.toBlob(resolve));
      if (!blob) return null;
      const blobUrl = URL.createObjectURL(blob);
      if (supportModulesElement) {
        supportModulesElement.removeChild(canvasElement);
      }

      return blobUrl;
    } catch (error) {
      console.error('Failed to get thumbnail: ', error);
      return null;
    }
  };

  /**
   * Loads the diagnostic support modules and attaches a rendered thumbnail to each one. Fetches the modules tagged DIAGNOSTIC_SUPPORT_MODULES, renders every module's PDF first page into a blob URL in parallel and assigns it onto the module, stores the list in state, logs failures and always clears the loading flag.
   *
   * @returns Resolves once the modules are stored and loading is cleared.
   */
  const handleDataLoad = async () => {
    try {
      const supportModules = await fetchSupportModules({ tag: 'DIAGNOSTIC_SUPPORT_MODULES' });
      await Promise.all(
        (supportModules ?? []).map(async (supportModule: any) => {
          const thumbnail = await handleGetThumbnail(supportModule.pdfUrl);
          supportModule.thumbnail = thumbnail;
        }),
      );
      setSupportModules(supportModules ?? []);
    } catch (error) {
      console.error('Failed to load support modules: ', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Opens the detail view for the support module the user pressed View on. Stores that module as the clicked support module, which swaps the grid for the details component.
   *
   * @param supportModule - Support module chosen from the grid.
   * @returns Nothing.
   */
  const handleViewClick = (supportModule: any) => {
    setClickedSupportModule(supportModule);
  };

  /**
   * Returns the support modules tab from a module's detail view back to the grid listing. Clears the clicked support module so nothing is selected.
   *
   * @returns Nothing.
   */
  const handleBackClick = () => {
    setClickedSupportModule(null);
  };

  useEffect(() => {
    handleDataLoad();
  }, []);

  const hasNoData = !isLoading && supportModules.length === 0;

  return {
    isLoading,
    supportModules,
    clickedSupportModule,
    handleViewClick,
    handleBackClick,
    hasNoData,
  };
};
